import { Segmented } from 'antd'
import type { MenuProps } from 'antd'
import {
  Bot,
  Building2,
  CreditCard,
  FileSearch,
  Globe,
  Home,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Monitor,
  Moon,
  Network,
  Plug,
  Plus,
  Settings,
  Share2,
  ShieldCheck,
  SunMedium,
  Tags,
  User,
  Users
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { CurrentAccountUser } from 'src/api/main'
import { useFeatureFlagsReady } from 'src/feature-flags'
import {
  useFeatureAgents,
  useFeatureBilling,
  useFeatureCompanyGraph,
  useFeatureDomains,
  useFeatureHomeAi,
  useFeatureMcpConnectors,
  useFeaturePublicAccess,
  useFeatureRoles
} from 'src/flags'
import { useWorkspacePermissionsContext } from 'src/hooks/useWorkspacePermissionsContext'
import { paths } from 'src/router'
import { useThemeMode } from 'src/theme'
import type { ThemePreference } from 'src/theme'
import { isWorkspaceMemberOfSession } from 'src/utils/workspaceMenuAccess'
import { getAllowedWorkspaceMenuKeys } from 'src/utils/workspacePermissions'

export type WorkspaceMenuTagTone =
  | 'default'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'

export type WorkspaceMenuTag = {
  label: string
  tone?: WorkspaceMenuTagTone
}

export type WorkspaceMenuItem = {
  key: string
  icon: ReactNode
  label: string
  path: string
  /** Item renders but is non-interactive; shown with a "Coming soon" badge. */
  disabled?: boolean
  /** Optional pill (AI / Trial / Beta) shown after the label when expanded. */
  tag?: WorkspaceMenuTag
}

export type WorkspaceMenuGroup = {
  key: string
  label: string
  items: WorkspaceMenuItem[]
}

export type WorkspaceMenuModel = {
  groups: WorkspaceMenuGroup[]
}

type UseWorkspaceMenuArgs = {
  workspaceId: number | undefined
  user: CurrentAccountUser | undefined
}

/** Grouped workspace sidebar model — matches example IA (Workspace / Identity / Applications / Developers / Compliance / Settings). */
export const useWorkspaceMenu = ({
  workspaceId,
  user
}: UseWorkspaceMenuArgs): WorkspaceMenuModel => {
  const { t } = useTranslation()
  const flagsReady = useFeatureFlagsReady()
  const domainsEnabled = useFeatureDomains()
  const billingEnabled = useFeatureBilling()
  const companyGraphEnabled = useFeatureCompanyGraph()
  const agentsEnabled = useFeatureAgents()
  const homeAiEnabled = useFeatureHomeAi()
  const rolesEnabled = useFeatureRoles()
  const publicAccessEnabled = useFeaturePublicAccess()
  const mcpConnectorsEnabled = useFeatureMcpConnectors()
  const { permissions } = useWorkspacePermissionsContext()

  return useMemo(() => {
    if (!workspaceId) {
      // Brand-new account with no workspaces yet: offer a way forward instead
      // of a blank sidebar (the switcher renders a matching placeholder above).
      // Gate on the real "zero workspaces" signal, not just an unresolved id —
      // a user who owns workspaces but has no active one selected should keep
      // the old (empty) behavior, not see a misleading create CTA.
      if ((user?.organizations ?? []).length === 0) {
        return {
          groups: [
            {
              key: 'getting-started',
              label: t('shell.menu.groups.gettingStarted'),
              items: [
                {
                  key: 'create-workspace',
                  icon: <Plus size={16} />,
                  label: t('shell.menu.createWorkspace'),
                  path: paths.workspaces.new
                }
              ]
            }
          ]
        }
      }

      return { groups: [] }
    }

    // A workspace id in the URL that the signed-in profile isn't a member of
    // (e.g. a deep link into someone else's workspace) must not render any
    // workspace nav — the page itself shows the "wrong account" screen
    // (WorkspaceAccessRoute). Without this, a missing `access_role` resolves to
    // the permissive "full" tier and would show the entire menu.
    if (!isWorkspaceMemberOfSession(user, workspaceId)) {
      return { groups: [] }
    }

    const id = String(workspaceId)
    const allowed = getAllowedWorkspaceMenuKeys(permissions)

    const blueprint: WorkspaceMenuGroup[] = [
      {
        key: 'workspace',
        label: t('shell.menu.groups.workspace'),
        items: [
          {
            key: 'home',
            icon: <Home size={16} />,
            label: t('shell.menu.home'),
            path: paths.workspaces.home(id),
            tag: { label: t('shell.menu.tags.ai'), tone: 'success' }
          },
          {
            key: 'overview',
            icon: <LayoutDashboard size={16} />,
            label: t('shell.menu.overview'),
            path: paths.workspaces.overview(id)
          }
        ]
      },
      {
        key: 'identity',
        label: t('shell.menu.groups.identity'),
        items: [
          {
            key: 'users',
            icon: <Users size={16} />,
            label: t('shell.menu.users'),
            path: paths.workspaces.members(id)
          },
          {
            key: 'organization',
            icon: <Building2 size={16} />,
            label: t('shell.menu.organization'),
            path: paths.workspaces.organization(id)
          },
          {
            key: 'security',
            icon: <ShieldCheck size={16} />,
            label: t('shell.menu.authentication'),
            path: paths.workspaces.security(id)
          },
          {
            key: 'roles',
            icon: <Tags size={16} />,
            label: t('shell.menu.roles'),
            path: paths.workspaces.roles(id)
          }
        ]
      },
      {
        key: 'applications',
        label: t('shell.menu.groups.applications'),
        items: [
          {
            key: 'apps',
            icon: <LayoutGrid size={16} />,
            label: t('shell.menu.apps'),
            path: paths.workspaces.apps.root(id)
          },
          {
            key: 'agents',
            icon: <Bot size={16} />,
            label: t('shell.menu.agents'),
            path: paths.workspaces.agents(id),
            tag: { label: t('shell.menu.tags.ai'), tone: 'success' }
          }
        ]
      },
      {
        key: 'developers',
        label: t('shell.menu.groups.developers'),
        items: [
          {
            key: 'publicAccess',
            icon: <Share2 size={16} />,
            label: t('shell.menu.publicAccess'),
            path: paths.workspaces.publicAccess(id)
          },
          {
            key: 'aiConnectors',
            icon: <Plug size={16} />,
            label: t('shell.menu.aiConnectors'),
            path: paths.workspaces.aiConnectors(id),
            tag: { label: t('shell.menu.tags.ai'), tone: 'success' }
          },
          {
            key: 'domains',
            icon: <Globe size={16} />,
            label: t('shell.menu.domains'),
            path: paths.workspaces.domains(id)
          }
        ]
      },
      {
        key: 'compliance',
        label: t('shell.menu.groups.compliance'),
        items: [
          {
            key: 'audit',
            icon: <FileSearch size={16} />,
            label: t('shell.menu.auditLog'),
            path: paths.workspaces.audit(id)
          },
          {
            key: 'companyGraph',
            icon: <Network size={16} />,
            label: t('shell.menu.companyGraph'),
            path: paths.workspaces.companyGraph(id),
            tag: { label: t('shell.menu.tags.preview'), tone: 'warning' }
          }
        ]
      },
      {
        key: 'settings',
        label: t('shell.menu.groups.settings'),
        items: [
          {
            key: 'workspaceSettings',
            icon: <Settings size={16} />,
            label: t('shell.menu.workspaceSettings'),
            path: paths.workspaces.settings(id)
          },
          {
            key: 'billing',
            icon: <CreditCard size={16} />,
            label: t('shell.menu.billing'),
            path: paths.workspaces.billing(id)
          }
        ]
      }
    ]

    // Feature-flag gate: hide items whose Unleash flag is OFF (fail-closed).
    // Until Unleash resolves, treat every gated surface as OFF — mirroring the
    // route guard (WorkspaceFeatureRoute), which shows a loader until the real
    // value is known rather than acting on the bootstrap default. The matching
    // route is gated too, so a hidden item is not reachable via a deep link.
    const flagGatedOff = new Set<string>()
    if (!flagsReady || !domainsEnabled) flagGatedOff.add('domains')
    if (!flagsReady || !billingEnabled) flagGatedOff.add('billing')
    if (!flagsReady || !companyGraphEnabled) flagGatedOff.add('companyGraph')
    if (!flagsReady || !agentsEnabled) flagGatedOff.add('agents')
    if (!flagsReady || !homeAiEnabled) flagGatedOff.add('home')
    if (!flagsReady || !rolesEnabled) flagGatedOff.add('roles')
    // Public Access + Domains are the only "developers" items, so with both
    // flags OFF the empty-group filter below drops that group header too.
    if (!flagsReady || !publicAccessEnabled) flagGatedOff.add('publicAccess')
    if (!flagsReady || !mcpConnectorsEnabled) flagGatedOff.add('aiConnectors')

    const groups = blueprint
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) => allowed.has(item.key) && !flagGatedOff.has(item.key)
        )
      }))
      .filter((group) => group.items.length > 0)

    return { groups }
  }, [
    t,
    user,
    workspaceId,
    permissions,
    flagsReady,
    domainsEnabled,
    billingEnabled,
    companyGraphEnabled,
    agentsEnabled,
    homeAiEnabled,
    rolesEnabled,
    publicAccessEnabled,
    mcpConnectorsEnabled
  ])
}

const appearanceOptions: { key: ThemePreference; icon: ReactNode }[] = [
  { key: 'light', icon: <SunMedium size={14} /> },
  { key: 'dark', icon: <Moon size={14} /> },
  { key: 'system', icon: <Monitor size={14} /> }
]

/**
 * The Light / Dark / System switcher of the account menu — a one-line
 * Segmented in a group row, as in ebs-platform-web's profile menu. Segments
 * keep their full label (`minWidth: 'max-content'`) so a longer language
 * ("Системная", "Întunecat") widens the menu past its 260px minimum instead
 * of ellipsizing.
 */
export const useAppearanceMenuItem = (): NonNullable<
  MenuProps['items']
>[number] => {
  const { t } = useTranslation()
  const { preference, setPreference } = useThemeMode()

  return useMemo(
    () => ({
      key: 'appearance',
      type: 'group',
      label: (
        <Segmented<ThemePreference>
          block
          size="small"
          styles={{ item: { minWidth: 'max-content' } }}
          aria-label={t('shell.topbar.theme')}
          value={preference}
          onChange={setPreference}
          options={appearanceOptions.map(({ key, icon }) => ({
            value: key,
            icon,
            label: t(`shell.topbar.themeMode.${key}`)
          }))}
        />
      )
    }),
    [t, preference, setPreference]
  )
}

/** User dropdown items rendered in the topbar and the focused shell. */
export const useUserDropdownItems = (
  onAccountSettings: () => void,
  onSignOut: () => void
): MenuProps['items'] => {
  const { t } = useTranslation()
  const appearanceItem = useAppearanceMenuItem()

  return useMemo(
    () => [
      {
        key: 'account',
        icon: <User size={16} />,
        label: t('shell.user.accountSettings'),
        onClick: onAccountSettings
      },
      { type: 'divider' as const },
      appearanceItem,
      { type: 'divider' as const },
      {
        key: 'logout',
        icon: <LogOut size={16} />,
        label: t('shell.user.signOut'),
        danger: true,
        onClick: onSignOut
      }
    ],
    [t, onAccountSettings, onSignOut, appearanceItem]
  )
}
